import type { KeyboardEvent } from 'react';
import type { AccountHolder } from '../model/types';
import { Card } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Logo } from '@/shared/ui/logo';
import { Button } from '@/shared/ui/button';
import { useLanguage } from '@/shared/hooks/use-language';

interface AccountHolderSelectorProps {
  accountHolders: AccountHolder[];
  selectedHolder: AccountHolder | null;
  onSelect: (holder: AccountHolder) => void;
  onExit?: () => void;
  isLoading?: boolean;
}

function activateOnKey(event: KeyboardEvent<HTMLDivElement>, activate: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  event.preventDefault();
  activate();
}

export function AccountHolderSelector({
  accountHolders,
  selectedHolder,
  onSelect,
  onExit,
  isLoading = false
}: AccountHolderSelectorProps) {
  const { t } = useLanguage();
  const activeHolders = accountHolders.filter(holder => holder.status === 'active');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-2xl p-8">
          <div className="text-center">
            <Logo className="justify-center mb-4" />
            <p className="text-muted-foreground">{t("accountHolder.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (activeHolders.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-2xl p-8">
          <div className="text-center">
            <Logo className="justify-center mb-4" />
            <p className="text-muted-foreground">{t("accountHolder.noHolders")}</p>
            {onExit && (
              <Button variant="ghost" onClick={onExit} className="mt-4">
                {t("accountHolder.backToLogin")}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-8">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {t("accountHolder.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("accountHolder.subtitle")}
          </p>
        </div>
        
        <div className="space-y-4">
          {activeHolders.map((holder) => (
            <Card
              key={holder.id}
              role="button"
              tabIndex={0}
              aria-pressed={selectedHolder?.id === holder.id}
              onClick={() => onSelect(holder)}
              onKeyDown={(event) => activateOnKey(event, () => onSelect(holder))}
              className={`bg-card border-border p-6 cursor-pointer transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                selectedHolder?.id === holder.id 
                  ? 'border-primary border-2 shadow-lg' 
                  : 'card-hover'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-foreground">
                      {holder.name}
                    </h3>
                    <Badge className="bg-green-500/10 text-green-500">
                      {t("accountHolder.status")}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("accountHolder.taxId")} {holder.tax_id}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
